import { ReactElement, useState } from 'react';
import config from 'config';
import parse from 'html-react-parser';
import Image from 'next/image';

import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useBoolean,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { ProviderInterface } from 'types';

import { Loader } from 'ui';

const {
  booking: { DefaultProviderAvatar }
} = config;

interface ProviderInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  provider: ProviderInterface;
}

const ProviderInfoModal = ({
  isOpen,
  onClose,
  provider
}: ProviderInfoModalProps): ReactElement => {
  const {
    colors: {
      palette: {
        booking: {
          stepService: {
            serviceList: { infoModal }
          }
        }
      }
    }
  } = useTheme();
  const [imageError, setImageError] = useState<boolean>(false);
  const [loading, setLoading] = useBoolean(true);

  const {
    public_name = '',
    public_title = '',
    public_quote = '',
    public_dscr = '',
    public_photo_url = '',
    name = ''
  } = provider;

  let formattedDescription = public_dscr
    ? public_dscr.replace(/\r\n/g, '<br />')
    : '';
  formattedDescription = `${formattedDescription}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'sm', md: 'md', lg: 'lg' }}
      isCentered
    >
      <ModalOverlay bg='blackAlpha.600' />
      <ModalContent
        borderRadius='12px'
        margin='15px'
        maxW={{ base: '90%', md: '600px', lg: '700px' }}
      >
        <ModalCloseButton
          bg={infoModal.closeButtonBackground}
          color={infoModal.closeButtonColor}
          _hover={{
            bg: infoModal.closeButtonBackground
          }}
        />
        <ModalBody p={0} maxH='80vh' overflowY='auto'>
          <Stack p={{ base: '16px', md: '24px' }}>
            <Flex alignItems='center' gap='20px'>
              <Box
                flexShrink={0}
                display='flex'
                justifyContent={{ base: 'center', md: 'flex-start' }}
              >
                <Box
                  width='120px'
                  height='120px'
                  borderRadius='full'
                  overflow='hidden'
                  bg='gray.200'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  {public_photo_url?.includes('http') && !imageError ? (
                    <>
                      {loading && <Loader absolute />}
                      <Image
                        sizes='(max-width: 450px) 65px, 120px'
                        src={public_photo_url}
                        alt={public_name || name || 'Provider'}
                        width={120}
                        height={120}
                        style={{ objectFit: 'cover' }}
                        onError={() => setImageError(true)}
                        onLoad={setLoading.off}
                      />
                    </>
                  ) : (
                    <DefaultProviderAvatar
                      width={120}
                      height={120}
                      fill='white'
                      style={{ opacity: 0.5 }}
                    />
                  )}
                </Box>
              </Box>

              <VStack align='start' spacing='5px' flex='1'>
                <Text
                  fontWeight='semibold'
                  fontSize={{ base: '18px', md: '20px' }}
                >
                  {public_name || name}
                </Text>
                {public_title && (
                  <Text
                    fontWeight='semibold'
                    fontSize={{ base: '14px', md: '16px' }}
                    color='gray.600'
                  >
                    {public_title}
                  </Text>
                )}

                {public_quote && (
                  <Text
                    fontStyle='italic'
                    fontSize={{ base: '12px', md: '14px' }}
                    color='gray.600'
                    mt='5px'
                  >
                    {parse(public_quote ?? '')}
                  </Text>
                )}
              </VStack>
            </Flex>

            {formattedDescription && (
              <Text fontSize='14px' lineHeight='160%' mt='16px'>
                {parse(formattedDescription ?? '')}
              </Text>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ProviderInfoModal;
